import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Modal } from 'react-bootstrap';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useParking } from '../../hooks/parking';
import { ParkingAreaInput } from '../../types/parking';
import ParkingAreaModal from './ParkingAreaModal';

interface BuildingChoice {
    key: string;
    name: string;
}

interface CreateParkingAreaFlowProps {
    buildings: BuildingChoice[];
    onHide: () => void;
    /** Called after the area is created (the page navigates to it). */
    onCreated?: () => void;
}

/**
 * "Nytt parkeringsområde" from the instance-wide Parkering page: pick the
 * building, then the ordinary ParkingAreaModal for that building. The
 * building is loaded into BuildingContext so the modal's level → room picker
 * and "Skapa rum" work exactly as on the building's own parking page.
 */
const CreateParkingAreaFlow: React.FC<CreateParkingAreaFlowProps> = ({ buildings, onHide, onCreated }) => {
    const navigate = useNavigate();
    const [buildingKey, setBuildingKey] = useState<string>(buildings.length === 1 ? buildings[0].key : '');
    const [chosen, setChosen] = useState(false);
    const { currentBuilding, fetchBuilding, loadingBuilding } = useBuilding();
    const { createArea, createRoom, submitting } = useParking({ buildingKey: chosen ? buildingKey : undefined });

    useEffect(() => {
        if (chosen && buildingKey && (!currentBuilding || currentBuilding.key !== buildingKey)) {
            void fetchBuilding(buildingKey);
        }
    }, [chosen, buildingKey, currentBuilding, fetchBuilding]);

    const buildingLoaded = chosen && !!currentBuilding && currentBuilding.key === buildingKey;

    const handleCreate = async (input: ParkingAreaInput) => {
        const area = await createArea(input);
        onCreated?.();
        navigate(`/building/${buildingKey}/parking/${area.key}`);
    };

    if (!chosen) {
        return (
            <Modal show onHide={onHide} centered>
                <Modal.Header closeButton><Modal.Title>Nytt parkeringsområde</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Byggnad</Form.Label>
                        <Form.Select value={buildingKey} onChange={e => setBuildingKey(e.target.value)} autoFocus>
                            <option value="">Välj byggnad…</option>
                            {buildings.map(b => <option key={b.key} value={b.key}>{b.name}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>Avbryt</Button>
                    <Button variant="primary" disabled={!buildingKey} onClick={() => setChosen(true)}>Fortsätt</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    if (!buildingLoaded) {
        return (
            <Modal show onHide={onHide} centered>
                <Modal.Body className="d-flex align-items-center gap-2">
                    <span className="spinner-border spinner-border-sm" />
                    <span>{loadingBuilding ? 'Läser in byggnaden…' : 'Byggnaden kunde inte läsas in.'}</span>
                </Modal.Body>
            </Modal>
        );
    }

    return (
        <ParkingAreaModal
            show
            onHide={onHide}
            onSubmit={handleCreate}
            structure={currentBuilding?.model.structure}
            onCreateRoom={createRoom}
            submitting={submitting}
        />
    );
};

export default CreateParkingAreaFlow;
